/**
 * Reading a SERVER-ONLY value at MODULE SCOPE, in a file that also gets bundled for the browser.
 *
 * WHY THIS IS NEEDED AT ALL. `db/index.ts` is shared: the client imports it so that entity classes and mutations are
 * registered on both sides. It pulls in the agent modules for their registration side effects, fifteen of those import
 * the model gate, and the gate imports the scheduling stack (`limits.ts` for its sizing, `load.ts` for admission,
 * `job-signal.ts` for cancellation). So those three modules are evaluated in the browser, and they are the only
 * server-only modules in the system that compute anything at module scope.
 *
 * Inside a function that is fine — nothing in the browser calls it. At MODULE SCOPE it is fatal: the whole bundle is
 * initialised eagerly, so the read throws while the page's first script is still running, taking the entire client
 * down with an error that names no module.
 *
 * There are THREE distinct sources, which is the reason this is a helper and not an inline `typeof` check:
 *
 *  - A shimmed built-in. The bundler replaces `node:os`, `node:v8` and `node:perf_hooks` with `undefined`, so
 *    `availableParallelism()` compiles to `(void 0)()` — a TypeError.
 *  - A server-only config value. `config.get` throws `ClientAccessError` by design for anything declared
 *    `client: false`, which is every job knob.
 *  - A Node-only property of a global that DOES exist in the browser. `process.cpuUsage` is absent from the
 *    bundler's `process` shim, so calling it is a TypeError even though `process` itself is there.
 *
 * A try/catch covers all three, and also covers a fourth case no guard could enumerate: a function missing on an
 * older Node. The fallback is never the value the SERVER uses — it exists so the module can finish evaluating in a
 * browser that will never read it.
 */

import { logger } from '@adornis/base/logging.js';

export function serverValue<T>(what: string, read: () => T, fallback: T): T {
  try {
    return read();
  } catch {
    // Debug, not warn: on the client this is the expected path and happens on every page load. A server that
    // somehow took it would show up immediately in the boot log, which prints every resolved limit.
    logger.debug({ what }, 'Server-only value unavailable — using the browser fallback');
    return fallback;
  }
}
